<!-- src/components/QuillLikeEditor.vue -->
<template>
  <div class="quill-like-editor" :class="{ 'quill-readonly': readonly }">
    <!-- 工具栏 -->
    <div v-if="showToolbar && !readonly" class="quill-toolbar">
      <span class="ql-formats">
        <button type="button" class="ql-bold" @click="format('bold')"></button>
        <button type="button" class="ql-italic" @click="format('italic')"></button>
        <button type="button" class="ql-underline" @click="format('underline')"></button>
        <button type="button" class="ql-strike" @click="format('strike')"></button>
      </span>
      
      <span class="ql-formats">
        <select class="ql-header" @change="format('header', $event.target.value)">
          <option value="1">H1</option>
          <option value="2">H2</option>
          <option value="3">H3</option>
          <option value="4">H4</option>
          <option value="5">H5</option>
          <option value="6">H6</option>
          <option value="0">正文</option>
        </select>
      </span>
      
      <span class="ql-formats">
        <button type="button" class="ql-list" value="bullet" @click="format('list', 'bullet')"></button>
        <button type="button" class="ql-list" value="ordered" @click="format('list', 'ordered')"></button>
      </span>
      
      <span class="ql-formats">
        <button type="button" class="ql-link" @click="insertLink"></button>
        <button type="button" class="ql-image" @click="insertImage"></button>
      </span>
      
      <span class="ql-formats">
        <button type="button" class="ql-code-block" @click="format('codeBlock')"></button>
        <button type="button" class="ql-blockquote" @click="format('blockquote')"></button>
      </span>
      
      <span class="ql-formats">
        <button type="button" class="ql-clean" @click="clearFormatting"></button>
      </span>
    </div>
    
    <!-- 编辑器内容区域 -->
    <div class="quill-editor" :style="{ minHeight: height + 'px' }">
      <editor-content 
        :editor="editor" 
        class="editor-content"
        @click="onEditorClick"
      />
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import { StarterKit } from '@tiptap/starter-kit'
import { Link } from '@tiptap/extension-link'
import { Image } from '@tiptap/extension-image'
import { TextAlign } from '@tiptap/extension-text-align'
import { Heading } from '@tiptap/extension-heading'
import { CodeBlock } from '@tiptap/extension-code-block'
import { Blockquote } from '@tiptap/extension-blockquote'

export default {
  name: 'QuillLikeEditor',
  
  components: {
    EditorContent,
  },
  
  props: {
    value: {
      type: String,
      default: ''
    },
    
    readonly: {
      type: Boolean,
      default: false
    },
    
    height: {
      type: [String, Number],
      default: 300
    },
    
    showToolbar: {
      type: Boolean,
      default: true
    }
  },
  
  data() {
    return {
      editor: null,
      isActive: {
        bold: false,
        italic: false,
        underline: false,
        strike: false,
        header: 0,
        list: '',
        link: false,
        image: false,
        codeBlock: false,
        blockquote: false
      }
    }
  },
  
  mounted() {
    this.initializeEditor()
  },
  
  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy()
    }
  },
  
  methods: {
    initializeEditor() {
      this.editor = new Editor({
        extensions: [
          StarterKit.configure({
            heading: {
              levels: [1, 2, 3, 4, 5, 6]
            }
          }),
          Link.configure({
            openOnClick: true,
            autolink: true,
          }),
          Image.configure({
            inline: true,
            allowBase64: true,
          }),
          TextAlign.configure({
            types: ['paragraph', 'heading'],
          }),
          CodeBlock,
          Blockquote,
        ],
        content: this.value,
        editable: !this.readonly,
        onUpdate: ({ editor }) => {
          this.$emit('input', editor.getHTML())
          this.$emit('change', editor.getHTML())
          this.updateActiveStates()
        },
        onFocus: ({ editor }) => {
          this.$emit('focus', editor)
          this.updateActiveStates()
        },
        onBlur: ({ editor }) => {
          this.$emit('blur', editor)
        },
        onSelectionUpdate: () => {
          this.updateActiveStates()
        }
      })
    },
    
    updateActiveStates() {
      if (!this.editor) return
      
      const { state } = this.editor
      const { from, to } = state.selection
      
      this.isActive = {
        bold: this.editor.isActive('bold'),
        italic: this.editor.isActive('italic'),
        underline: this.editor.isActive('underline'),
        strike: this.editor.isActive('strike'),
        header: this.editor.isActive('heading') ? 
                state.doc.resolve(from).node().attrs.level : 0,
        list: this.editor.isActive('bulletList') ? 'bullet' : 
               this.editor.isActive('orderedList') ? 'ordered' : '',
        link: this.editor.isActive('link'),
        image: this.editor.isActive('image'),
        codeBlock: this.editor.isActive('codeBlock'),
        blockquote: this.editor.isActive('blockquote')
      }
    },
    
    format(type, value) {
      if (!this.editor) return
      
      const chain = this.editor.chain().focus()
      
      switch (type) {
        case 'bold':
          chain.toggleBold().run()
          break
          
        case 'italic':
          chain.toggleItalic().run()
          break
          
        case 'underline':
          chain.toggleUnderline().run()
          break
          
        case 'strike':
          chain.toggleStrike().run()
          break
          
        case 'header':
          if (value === '0') {
            chain.setParagraph().run()
          } else {
            chain.toggleHeading({ level: parseInt(value) }).run()
          }
          break
          
        case 'list':
          if (value === 'bullet') {
            chain.toggleBulletList().run()
          } else {
            chain.toggleOrderedList().run()
          }
          break
          
        case 'codeBlock':
          chain.toggleCodeBlock().run()
          break
          
        case 'blockquote':
          chain.toggleBlockquote().run()
          break
          
        default:
          console.warn('Unknown format type:', type)
      }
    },
    
    insertLink() {
      const previousUrl = this.editor.getAttributes('link').href
      const url = window.prompt('请输入链接地址:', previousUrl)
      
      if (url === null) return
      
      if (url === '') {
        this.editor.chain().focus().unsetLink().run()
        return
      }
      
      this.editor.chain().focus().setLink({ href: url }).run()
    },
    
    insertImage() {
      const url = window.prompt('请输入图片地址:')
      if (url) {
        this.editor.chain().focus().setImage({ src: url }).run()
      }
    },
    
    clearFormatting() {
      this.editor.chain().focus()
        .clearNodes()
        .unsetAllMarks()
        .run()
    },
    
    onEditorClick() {
      this.updateActiveStates()
    },
    
    getHTML() {
      return this.editor ? this.editor.getHTML() : ''
    },
    
    getText() {
      return this.editor ? this.editor.getText() : ''
    },
    
    setContent(content) {
      if (this.editor) {
        this.editor.commands.setContent(content)
      }
    },
    
    insertContent(content) {
      if (this.editor) {
        this.editor.commands.insertContent(content)
      }
    }
  },
  
  watch: {
    value(newVal) {
      if (this.editor && this.editor.getHTML() !== newVal) {
        this.editor.commands.setContent(newVal, false)
        this.$nextTick(() => {
          this.updateActiveStates()
        })
      }
    },
    
    readonly(newVal) {
      if (this.editor) {
        this.editor.setEditable(!newVal)
      }
    }
  }
}
</script>

<style>
.quill-like-editor {
  border: 1px solid #ccc;
  border-radius: 2px;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.1);
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 16px;
  line-height: 1.42;
  color: #333;
}

.quill-readonly {
  border-color: #e5e5e5;
}

.quill-toolbar {
  background-color: #f1f1f1;
  border-bottom: 1px solid #ccc;
  padding: 8px 8px 7px;
  user-select: none;
}

.ql-formats {
  display: inline-block;
  margin-right: 8px;
}

.ql-formats:last-child {
  margin-right: 0;
}

.quill-toolbar button,
.quill-toolbar select {
  background-color: #fff;
  border: 1px solid #ccc;
  color: #333;
  cursor: pointer;
  display: inline-block;
  float: left;
  height: 24px;
  margin-right: 2px;
  padding: 0;
  width: 28px;
  border-radius: 2px;
  font-size: 14px;
  line-height: 24px;
  text-align: center;
}

.quill-toolbar button:hover,
.quill-toolbar select:hover {
  background-color: #ebebeb;
  border-color: #bbb;
}

.quill-toolbar button:active,
.quill-toolbar select:active {
  background-color: #e0e0e0;
  border-color: #adadad;
}

.quill-toolbar .ql-bold::before {
  content: 'B';
  font-weight: bold;
}

.quill-toolbar .ql-italic::before {
  content: 'I';
  font-style: italic;
}

.quill-toolbar .ql-underline::before {
  content: 'U';
  text-decoration: underline;
}

.quill-toolbar .ql-strike::before {
  content: 'S';
  text-decoration: line-through;
}

.quill-toolbar .ql-list[value="bullet"]::before {
  content: '•';
}

.quill-toolbar .ql-list[value="ordered"]::before {
  content: '123';
}

.quill-toolbar .ql-link::before {
  content: '🔗';
}

.quill-toolbar .ql-image::before {
  content: '🖼️';
}

.quill-toolbar .ql-code-block::before {
  content: '{}';
}

.quill-toolbar .ql-blockquote::before {
  content: '"';
}

.quill-toolbar .ql-clean::before {
  content: '⌫';
}

.quill-toolbar .ql-bold.ql-active,
.quill-toolbar .ql-italic.ql-active,
.quill-toolbar .ql-underline.ql-active,
.quill-toolbar .ql-strike.ql-active,
.quill-toolbar .ql-link.ql-active,
.quill-toolbar .ql-image.ql-active,
.quill-toolbar .ql-code-block.ql-active,
.quill-toolbar .ql-blockquote.ql-active {
  background-color: #007aff;
  border-color: #007aff;
  color: white;
}

.quill-toolbar select {
  height: 26px;
  width: auto;
  padding: 0 5px;
  min-width: 48px;
  border-radius: 2px;
  font-size: 14px;
}

.quill-toolbar select.ql-header {
  width: 60px;
}

.quill-editor {
  min-height: 300px;
  background-color: #fff;
}

.editor-content {
  height: 100%;
  padding: 12px;
  outline: none;
}

.ProseMirror {
  height: 100%;
  line-height: 1.42;
}

.ProseMirror p {
  margin: 0;
  padding: 0;
}

.ProseMirror h1 {
  font-size: 2em;
  margin: 0.67em 0;
}

.ProseMirror h2 {
  font-size: 1.5em;
  margin: 0.83em 0;
}

.ProseMirror h3 {
  font-size: 1.17em;
  margin: 0.83em 0;
}

.ProseMirror h4 {
  font-size: 1em;
  margin: 1.12em 0;
}

.ProseMirror h5 {
  font-size: 0.83em;
  margin: 1.5em 0;
}

.ProseMirror h6 {
  font-size: 0.67em;
  margin: 1.67em 0;
}

.ProseMirror ul,
.ProseMirror ol {
  padding-left: 1.5em;
  margin: 1em 0;
}

.ProseMirror li > p {
  margin: 0;
}

.ProseMirror img {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 1em auto;
}

.ProseMirror a {
  color: #007aff;
  text-decoration: underline;
}

.ProseMirror blockquote {
  border-left: 4px solid #ccc;
  margin: 1em 0;
  padding: 0.5em 1em;
  color: #666;
}

.ProseMirror pre {
  background-color: #f5f5f5;
  border: 1px solid #ccc;
  border-radius: 2px;
  padding: 1em;
  margin: 1em 0;
  overflow: auto;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 85%;
  line-height: 1.45;
}

.ProseMirror code {
  background-color: #f5f5f5;
  padding: 0.2em 0.4em;
  border-radius: 3px;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 85%;
}
</style>